import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output } from '@angular/core';
import {
  ColourGroupFields,
  CustomColourType,
  NewColourPaletteType,
  defaultColourPalette,
  getFixedPaletteGroup,
  getPaletteCssVariableName,
  legacyCustomColourVarKey,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import { cloneDeep } from 'lodash';
import { GlobalThemeState } from '../../../libs/state/global-theme.state';

const defaultVariablesList = [{ name: 'Use Color Picker', value: 'none' }];

@Component({
  standalone: false,
  selector: 'finxone-workflow-frontend-color-picker-variables',
  templateUrl: './color-picker-variables.component.html',
  styleUrls: ['./color-picker-variables.component.scss'],
})
export class ColorPickerVariablesComponent implements OnInit, OnChanges {
  @Input() public color: string;
  @Input() public pickerType: 'standard' | 'circle' = 'circle';
  @Output() public colorChange = new EventEmitter<string>();
  @Output() public colorPickerSelect = new EventEmitter<string>();
  public selectedBackgroundColor: string;
  public selectedVariableIndex = 0;
  public colorPickerActive = false;

  public variableColorList: { name: string; value: string }[] = [];
  public selectedVarName: string | undefined;

  constructor(private store: Store, private readonly cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.getActiveGlobalThemeColourConfig();
    this.refreshColorPickerVariablesList();
    this.setInitialData();
  }
  private setInitialData() {
    this.selectedBackgroundColor = this.color;
    if (this.color?.includes('var')) {
      const index = this.variableColorList.findIndex((option) => option.value === this.color);
      if (index !== -1) {
        this.selectedVariableIndex = index;
        if (this.pickerType === 'standard') {
          this.selectedVarName = this.variableColorList[index].name;
        }
      } else {
        this.colorPickerActive = true;
      }
    } else {
      this.colorPickerActive = true;
    }
  }

  ngOnChanges(): void {
    this.getActiveGlobalThemeColourConfig();
    this.setInitialData();
  }

  onChange(value: string, useVariable: boolean) {
    this.selectedVarName = undefined;
    if (useVariable) {
      if (value === 'none') {
        this.selectedVariableIndex = 0;
        this.colorPickerActive = true;
        return;
      }
      this.color = value;
      this.colorPickerActive = false;

      const index = this.variableColorList.findIndex((option) => option.value === this.color);
      if (index !== -1) {
        this.selectedVariableIndex = index;
        if (this.pickerType === 'standard') {
          this.selectedVarName = this.variableColorList[index].name;
        }
      }
      if (value.includes('var')) {
        const variable = value.substring(value.indexOf('(') + 1, value.indexOf(')'));
        const style = getComputedStyle(document.documentElement);
        this.selectedBackgroundColor = style.getPropertyValue(variable);
      }
      this.colorPickerSelect.emit(value);
      this.colorChange.emit(value);
    } else {
      if (this.selectedVariableIndex === 0 && this.colorPickerActive) {
        this.selectedBackgroundColor = value;
        this.color = value;
        this.colorPickerSelect.emit(value);
        this.colorChange.emit(value);
      }
    }
    this.cdr.detectChanges();
  }

  public get variableColorsList() {
    return this.variableColorList;
  }

  private getActiveGlobalThemeColourConfig() {
    const activeThemeConfig = this.store.selectSnapshot(GlobalThemeState.getActiveGlobalTheme);
    const paletteConfig = activeThemeConfig?.generalConfigurations?.palette ?? defaultColourPalette;
    if (paletteConfig) {
      const colorList = this.generateColorList(paletteConfig);
      const existingValues = new Set(this.variableColorList.map((item) => item.value));
      const uniqueColorList = colorList.filter((item) => !existingValues.has(item.value));
      this.variableColorList = [...this.variableColorList, ...uniqueColorList];
    }
  }

  /**
   * Walks the actual saved palette (every fixed and custom group, and every
   * colour within them) so the list matches what the user has configured on
   * the Global Styling colour palette, rather than a separately maintained
   * static list. Fixed-group labels come from `getFixedPaletteGroup`, the
   * same metadata the palette editor uses; custom groups/colours use their
   * key as the label, matching the editor's own convention.
   */
  private generateColorList(colorObject: NewColourPaletteType): { name: string; value: string }[] {
    const colorList: { name: string; value: string }[] = [];

    Object.entries(colorObject ?? {}).forEach(([groupKey, group]) => {
      if (groupKey === 'customColors' || Array.isArray(group)) return;
      const fixedGroup = getFixedPaletteGroup(groupKey);
      const groupLabel = fixedGroup?.label ?? groupKey;
      Object.keys(group as ColourGroupFields).forEach((colourKey) => {
        const colourLabel = fixedGroup?.colourLabels[colourKey] ?? colourKey;
        colorList.push({
          name: `${groupLabel} - ${colourLabel}`,
          value: `var(${getPaletteCssVariableName(groupKey, colourKey)})`,
        });
      });
    });

    colorObject?.customColors?.forEach((customColor: CustomColourType) => {
      colorList.push({
        name: `Custom Color - ${customColor.name}`,
        value: `var(--color-custom-${legacyCustomColourVarKey(customColor.name)})`,
      });
    });

    return colorList;
  }

  public refreshColorPickerVariablesList(event?: Event) {
    this.variableColorList = [];
    this.variableColorList = cloneDeep(defaultVariablesList);
    this.getActiveGlobalThemeColourConfig();
    this.setInitialData();
    this.cdr.detectChanges();
    event?.stopPropagation();
  }
}
